import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { listChannels } from '../services/agentChannelApi';
import NotificationSoundManager from '../utils/NotificationSoundManager';
import UserStreamManager from '../utils/UserStreamManager';
import eventBus from '../utils/eventBus';
import { useAuth } from './AuthContext';

// Support V1 phase 5 (REQUIREMENTS.md 9.3): support chats are out of DM search and the DM browse list (the Support app has
// its own search); they reach the sidebar only through its support rule (sidebarWindow.js).
export const searchableDms = (channels) => (channels || []).filter((ch) => ch && ch.is_dm && !ch.is_support);

/**
 * ChannelListContext — the Agent-Channels analog of ChatListContext/EmployeeListContext, mounted
 * ONCE globally so channel state survives the sidebar section being folded or the whole sidebar
 * collapsing to the rail. This is load-bearing for the attention surfaces (Nitish, 2026-08-01):
 *   1. The Channels SECTION-HEADER dot (folded section) needs channel data while
 *      SidebarChannelList is UNMOUNTED (GlobalSidebar only renders the list when the section is
 *      open) — with the old component-local state a folded section could never show attention.
 *   2. The mention BELL must ring on a new @mention even while the section is folded — so the
 *      ring logic lives HERE, not in the list component.
 * Data flow: one fetch + CHANNEL_CHANGED debounced refetch (fires for every post, org-fanout) +
 * 'channel-read' eventBus refetch (mark-read is a per-viewer write — no broadcast fires;
 * ChannelPage/ChannelThreadPanel emit the nudge right after advancing a cursor so dot/badge
 * clear instantly).
 *
 * CEL-795 (Slack UX): this context also owns the OPEN THREAD (D2) — the thread panel renders in
 * the right slot via MediaViewerPanel's fall-through, OUTSIDE ChannelPage, so the state must live
 * globally — and exposes the server-owned mention candidates (the panel's composer needs them
 * without page plumbing — F3).
 *
 * CEL-875 §F2: the inline archived-rows toggle (old PRD D7) is RETIRED — archived channels live
 * in the Archived-channels MODAL (SidebarChannelList owns it; it fetches include_archived=1 on
 * open). `channels` here is LIVE + JOINED-ONLY forever — every consumer (bell ring loop, badges,
 * header dot, thread panel lookups) keeps its exact semantics.
 */
const ChannelListContext = createContext();

export const ChannelListProvider = ({ children }) => {
  const [channels, setChannels] = useState([]);
  // Membership v2 (§B): live channels the viewer has NOT joined — the Browse modal's list.
  const [browseChannels, setBrowseChannels] = useState([]);
  // dm_ownership PRD F4: EVERY viewable DM (joined + observable) — the Browse modal's
  // "Direct messages" tab. The sidebar keeps joined-only; this is the observability surface.
  const [allDms, setAllDms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mentionCandidates, setMentionCandidates] = useState([]);
  // D2: the open thread — { channelId, rootId } | null. Set by ChannelPage (chip / hover action /
  // ?thread= deep link); rendered by MediaViewerPanel's fall-through; cleared on close/route-away.
  const [openThread, setOpenThread] = useState(null);
  // Sidebar window (Oct 19 2026): conversations opened in THIS session stay in the sidebar until a refresh.
  const [sessionOpenedIds, setSessionOpenedIds] = useState(() => new Set());
  const { currentUser, userProfile, canHireAiEmployees } = useAuth();
  const refetchTimerRef = useRef(null);

  // CEL-789 → human_message_notification_sound_prd.md: snapshot of each channel's
  // mention_pending_at stamp (null = no pending ping). A CHANGED non-null stamp observed LIVE
  // rings — the BE re-stamps the timestamp on EVERY ping, so each new pinging message rings
  // (WhatsApp parity, Nitish 2026-08-10; the old boolean false→true transition suppressed
  // re-pings). null until the first fetch lands, so a page load with an already-pending
  // mention shows the dot without a startle ring.
  const mentionPendingRef = useRef(null);
  // The channel the user is LOOKING AT never rings (they're already attending; the page's
  // mark-read clears the pending flag moments later — ringing would be noise). ChannelPage
  // registers itself via setActiveChannelId on mount/route change.
  const activeChannelIdRef = useRef(null);

  // Channels are an agent-team surface: same gate as the sidebar section (canHireAiEmployees).
  const enabled = !!currentUser && userProfile?.onboarding_status === 'JOINED' && !!canHireAiEmployees;

  const fetchChannels = useCallback(async () => {
    try {
      const res = await listChannels({});
      const live = (res.data.channels || []).filter((ch) => !ch.is_archived);
      // Membership v2 (channel_membership_v2_prd.md §B — Nitish 2026-08-04): the sidebar is
      // JOINED-ONLY, Slack-style — joined channels plus mention alerts in unjoined ones
      // (#general is always joined implicitly). Everything else moves to the Browse modal.
      // CEL-875 §F4: opening a channel no longer joins it, so a previewed channel simply
      // doesn't persist here — joining is the one explicit write.
      // CEL-1041: DMs are people-rooms — participants' DMs ALWAYS render (a DM is never
      // "unjoined"); Join is meaningless on a DM (participants are fixed; the server 409s it).
      // The SIDEBAR stays joined-only (your own conversations); ALL viewable DMs — yours plus
      // the observable agent-only ones under teams you lead — feed the Browse modal's
      // "Direct messages" tab (dm_ownership_and_observability_prd.md F4: D11 opened).
      const list = live.filter((ch) => ch.is_dm ? ch.joined : (ch.joined || ch.mention_pending));
      // #general pinned first, then most recently active (the API's order within the rest).
      list.sort((a, b) => (b.is_general ? 1 : 0) - (a.is_general ? 1 : 0));
      setBrowseChannels(live.filter((ch) => !ch.is_dm && !ch.joined && !ch.mention_pending));
      setAllDms(searchableDms(live));

      // Ring PER PINGING MESSAGE (human_message_notification_sound_prd.md, never on initial
      // load): every ping re-stamps mention_pending_at server-side, so a changed stamp = a new
      // message → ring, voiced by the pinging author's kind ('human' = the warm DM chime,
      // 'agent'/unknown = the classic bell — fail-safe on old payloads). Same sound manager as
      // chats — leader-tab election + mute + the 500ms anti-burst floor all come for free.
      // The channel being VIEWED stays silent (the user is reading it; mark-read clears the
      // ping moments later). 'pending' sentinel: an old BE without mention_pending_at degrades
      // to ring-once-per-pending — exactly the pre-PRD behavior.
      const prev = mentionPendingRef.current;
      if (prev) {
        list.forEach((ch) => {
          const stamp = ch.mention_pending ? (ch.mention_pending_at || 'pending') : null;
          if (stamp && prev[ch.id] !== stamp && ch.id !== activeChannelIdRef.current) {
            NotificationSoundManager.play(
              `channel-${ch.id}`,
              ch.mention_pending_author_kind === 'human' ? 'human' : 'agent'
            );
          }
        });
      }
      mentionPendingRef.current = Object.fromEntries(
        list.map((ch) => [ch.id, ch.mention_pending ? (ch.mention_pending_at || 'pending') : null])
      );

      setChannels(list);
      if (res.data.mention_candidates) setMentionCandidates(res.data.mention_candidates);
    } catch (e) {
      console.error('[ChannelList] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setChannels([]);
      setLoading(false);
      return undefined;
    }
    fetchChannels();
    const unsub = UserStreamManager.subscribe('CHANNEL_CHANGED', () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(fetchChannels, 400);
    });
    // ChannelPage/ChannelThreadPanel emit this right after advancing a read cursor — refetch so
    // the attention dot + unread badge clear the moment the user opens the channel/thread.
    const offRead = eventBus.on ? eventBus.on('channel-read', fetchChannels) : null;
    return () => {
      unsub();
      if (offRead) offRead();
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [enabled, fetchChannels]);

  const setActiveChannelId = useCallback((id) => {
    activeChannelIdRef.current = id || null;
    // Opening is not activity, but an opened conversation stays in the sidebar for this session (sidebarWindow.js).
    if (id) setSessionOpenedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const forgetSessionChannel = useCallback((id) => {
    setSessionOpenedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const openChannelThread = useCallback((channelId, rootId) => {
    setOpenThread(channelId && rootId ? { channelId, rootId } : null);
  }, []);

  const closeChannelThread = useCallback(() => {
    setOpenThread(null);
  }, []);

  return (
    <ChannelListContext.Provider value={{
      channels,
      browseChannels,
      allDms,
      loading,
      refreshChannels: fetchChannels,
      setActiveChannelId,
      sessionOpenedIds,
      forgetSessionChannel,
      mentionCandidates,
      openThread,
      openChannelThread,
      closeChannelThread,
    }}>
      {children}
    </ChannelListContext.Provider>
  );
};

export const useChannelList = () => {
  const ctx = useContext(ChannelListContext);
  if (!ctx) {
    // Safe no-op fallback if used outside the provider (defensive default).
    return {
      channels: [],
      browseChannels: [],
      allDms: [],
      loading: false,
      refreshChannels: () => {},
      setActiveChannelId: () => {},
      sessionOpenedIds: new Set(),
      forgetSessionChannel: () => {},
      mentionCandidates: [],
      openThread: null,
      openChannelThread: () => {},
      closeChannelThread: () => {},
    };
  }
  return ctx;
};

export default ChannelListContext;
